// DeskStructure — the structure beside an open document (#161, 3): the
// course or module it was opened from, as a narrow column. In a course every
// module is a heading (open it to see the module) with its documents under
// it (1.1, 1.2 …); in a module, its documents. Any row opens in place, so
// moving between structure and detail never leaves focus. Previous / next
// walk the same list, across module boundaries. Pure.

export { DeskStructure, structureRows };

import type { ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import type { ContainerCard, ContainerPart } from "../Studio/studioCards.js";
import { partIcon } from "./DeskRows.js";

type StructureRow = {
    readonly type: "module" | "document";
    readonly id: string;
    readonly number: string;
    readonly title: string;
    readonly planned: boolean;
    readonly icon: IconName;
};

const rowOf = (
    type: StructureRow["type"],
    p: ContainerPart,
    number: string,
): StructureRow => ({
    type,
    id: p.id,
    number,
    title: p.title,
    planned: p.planned,
    icon: partIcon(p),
});

// The rows of `root` in reading order: a course's modules each followed by
// their documents, a module's documents.
const structureRows = (root: ContainerCard): readonly StructureRow[] => {
    const course = root.parts.some((p) => p.kind === "module");
    const docs = root.parts.filter((p) => p.kind !== "module");
    const modules = root.parts.filter((p) => p.kind === "module");
    const own = docs.map((p, i) => rowOf("document", p, String(i + 1)));
    if (!course) return own;
    return [
        ...modules.flatMap((m, i) => [
            rowOf("module", m, String(i + 1)),
            ...m.parts
                .filter((p) => p.kind !== "module")
                .map((p, j) => rowOf("document", p, `${i + 1}.${j + 1}`)),
        ]),
        ...own,
    ];
};

const DeskStructure = ({
    root,
    rows,
    current,
    onOpen,
}: {
    // The course or module the document was opened from.
    readonly root: ContainerCard;
    readonly rows: readonly StructureRow[];
    // The document shown.
    readonly current: string;
    readonly onOpen: (id: string) => void;
}): ReactNode => (
    <nav className="bilda-desk__strip" aria-label={root.title}>
        <p className="bilda-desk__strip-title" title={root.title}>
            <Icon name={root.kind} size="sm" />
            {root.title}
        </p>
        <ol>
            {rows.map((row) => (
                <li key={row.id}>
                    <button
                        type="button"
                        className="bilda-desk__strip-row"
                        data-row={row.type}
                        aria-current={row.id === current ? "page" : undefined}
                        data-planned={row.planned ? "true" : undefined}
                        title={row.title}
                        onClick={() =>
                            row.id === current ? undefined : onOpen(row.id)
                        }
                    >
                        <span className="bilda-desk__strip-number">
                            {row.number}
                        </span>
                        <Icon name={row.icon} size="sm" />
                        <span className="bilda-desk__strip-text">
                            {row.title}
                        </span>
                    </button>
                </li>
            ))}
        </ol>
    </nav>
);
